import React, { useState, useEffect } from "react";
import * as customerService from "../../../Customers/services/customerService";

const JobForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState(
    initialData ? {
      customerId: initialData.customer?.id || "",
      title: initialData.title || "",
      description: initialData.description || "",
      status: initialData.status || "IN_PROGRESS",
      invoiceDate: initialData.startDate || initialData.orderDate || new Date().toISOString().split('T')[0],
    } : {
      customerId: "",
      title: "",
      description: "",
      status: "IN_PROGRESS",
      invoiceDate: new Date().toISOString().split('T')[0],
    }
  );

  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Filter customers based on search term
  useEffect(() => {
    if (customerSearchTerm.trim() === "") {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        customer.username?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerSearchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    }
  }, [customerSearchTerm, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await customerService.getCustomersList({ pageSize: 1000 });
      if (response && response.customers) {
        setCustomers(response.customers);
        setFilteredCustomers(response.customers);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.title.trim()) {
      return;
    }
    if (!formData.customerId) {
      return;
    }
    if (!formData.status) {
      return;
    }

    // Prepare data for submission
    const submissionData = {
      ...formData,
      title: formData.title.trim(),
      description: formData.description || "",
      startDate: formData.invoiceDate,
      customerId: parseInt(formData.customerId)
    };

    onSubmit(submissionData);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCustomerSearch = (e) => {
    setCustomerSearchTerm(e.target.value);
    setShowCustomerDropdown(true);
  };

  const handleCustomerSelect = (customer) => {
    setFormData({
      ...formData,
      customerId: customer.id
    });
    const customerDisplayName = customer.name || customer.username || "Unknown Customer";
    setCustomerSearchTerm(customerDisplayName);
    setSelectedCustomer(customer);
    setShowCustomerDropdown(false);
  };

  const handleClearCustomer = () => {
    setFormData({ ...formData, customerId: "" });
    setCustomerSearchTerm("");
    setSelectedCustomer(null);
  };

  const handleCustomerInputFocus = () => {
    setShowCustomerDropdown(true);
  };

  const handleCustomerInputBlur = () => {
    setTimeout(() => setShowCustomerDropdown(false), 200);
  };

  return (
    <div className="mb-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">
        {initialData ? "Edit Wholesale Order" : "New Wholesale Order"}
      </h2>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        {/* Selected Customer Card */}
        {selectedCustomer && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-gray-900">{selectedCustomer.name || "Unknown Customer"}</h4>
                  {selectedCustomer.customerType && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      selectedCustomer.customerType === 'WHOLESALE' ? 'bg-green-100 text-green-700' :
                      selectedCustomer.customerType === 'BOTH' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedCustomer.customerType === 'WHOLESALE' ? 'Wholesale' :
                       selectedCustomer.customerType === 'BOTH' ? 'Wholesale + Retail' :
                       selectedCustomer.customerType}
                    </span>
                  )}
                </div>
                {selectedCustomer.businessName && (
                  <p className="text-sm text-gray-600 mb-2">{selectedCustomer.businessName}</p>
                )}
                <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                  {selectedCustomer.phone && (
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      {selectedCustomer.phone}
                    </span>
                  )}
                  {(selectedCustomer.city || selectedCustomer.state) && (
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                      {[selectedCustomer.city, selectedCustomer.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                  {selectedCustomer.totalOrders > 0 && (
                    <span className="inline-flex items-center gap-1 text-blue-600">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                      {selectedCustomer.totalOrders} previous orders
                    </span>
                  )}
                </div>
                {selectedCustomer.addressLine1 && (
                  <p className="mt-2 text-xs text-gray-500">
                    <span className="font-medium">Address:</span> {selectedCustomer.addressLine1}
                    {selectedCustomer.addressLine2 && `, ${selectedCustomer.addressLine2}`}
                    {selectedCustomer.city && `, ${selectedCustomer.city}`}
                    {selectedCustomer.state && `, ${selectedCustomer.state}`}
                    {selectedCustomer.zip && ` ${selectedCustomer.zip}`}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleClearCustomer}
                className="text-gray-400 hover:text-gray-600 p-1"
                title="Change customer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer *
            </label>
            <input
              type="text"
              value={customerSearchTerm}
              onChange={handleCustomerSearch}
              onFocus={handleCustomerInputFocus}
              onBlur={handleCustomerInputBlur}
              placeholder={selectedCustomer ? "Change customer..." : "Search for customer..."}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                selectedCustomer ? 'border-green-300 bg-green-50' : 'border-gray-300'
              }`}
              required={!selectedCustomer}
            />
            {showCustomerDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => {
                    return (
                      <div
                        key={customer.id}
                        className="px-3 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{customer.name || "Unknown Customer"}</div>
                            {customer.businessName && (
                              <div className="text-sm text-gray-600">{customer.businessName}</div>
                            )}
                          </div>
                          {customer.customerType && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              customer.customerType === 'WHOLESALE' ? 'bg-green-100 text-green-700' :
                              customer.customerType === 'BOTH' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {customer.customerType === 'WHOLESALE' ? 'Wholesale' :
                               customer.customerType === 'BOTH' ? 'Wholesale + Retail' :
                               customer.customerType}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                          {customer.phone && (
                            <span className="inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                              {customer.phone}
                            </span>
                          )}
                          {(customer.city || customer.state) && (
                            <span className="inline-flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                              {[customer.city, customer.state].filter(Boolean).join(", ")}
                            </span>
                          )}
                          {customer.totalOrders > 0 && (
                            <span className="inline-flex items-center gap-1 text-blue-600">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                              {customer.totalOrders} orders
                            </span>
                          )}
                        </div>
                        {customer.addressLine1 && (
                          <div className="mt-1 text-xs text-gray-400 truncate">
                            {customer.addressLine1}{customer.city && `, ${customer.city}`}{customer.state && `, ${customer.state}`}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-4 text-gray-500 text-center">
                    {customers.length === 0
                      ? "No customers available. Please add a customer first."
                      : "No customers match your search."
                    }
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              placeholder="e.g., Weekly Jerky Order"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice Date *
            </label>
            <input
              type="date"
              name="invoiceDate"
              value={formData.invoiceDate}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add any notes about this order..."
          />
        </div>
        <div className="flex gap-3">
          <button
            type="submit"
            className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            {initialData ? "Update Order" : "Create Order"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
